import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import cx from "classnames";
import { Context, Hooks, Platform, Util } from "@xt/xtbase";
import useIsDesktop from "@/hooks/useIsDesktop";
import { Modal, Select, Spin, message } from "antd";
import corporateLogo from "assets/images/spikex-card/logo2.png";
import mastercardMark from "assets/images/spikex-card/mastercard-mark.png";
import affiliateArcDivider from "assets/images/referral/partner-level-ui/affiliate-horizontal-divider.png";
import benefitGlobe from "assets/images/spikex-card/benefit-globe.png";
import benefitCard from "assets/images/spikex-card/benefit-card.png";
import benefitUsd from "assets/images/spikex-card/benefit-usd.png";
import iconAttachmentRequired from "assets/images/spikex-card/icon-attachment-required.svg";
import iconRejected from "assets/images/spikex-card/icon-rejected.svg";
import ManageCardContent from "./ManageCardContent";
import modalStyles from "./spikexModal.module.less";
import styles from "./index.module.less";
import { getFirstRechargeAmount } from "./cardConfig";
import { get_inviteVipProgress } from "@/api/referral";
import {
  get_hyperpayCardApplyPrecheck,
  get_hyperpayCardBalance,
  get_hyperpayCardEligibility,
  get_hyperpayCardEntryStatus,
  get_hyperpayCardInfo,
  post_hyperpayCardApply,
  post_hyperpayCardApplyStatus,
  post_hyperpayCardAttachmentUpload,
  post_hyperpayCardSensitive,
} from "@/api/hyperpay-card";
import { get_resetKycInfo } from "@/api/uaapi/user";
import { openLiveHelpCustomerService } from "@/utils/customerService";
import { resolveHyperpayErrorMessage } from "@/utils/hyperpayErrors";
import {
  getHyperpayKycImageLimit,
  resolveHyperpayKycImageErrorKey,
  uploadHyperpayKycImage,
  validateHyperpayKycImageFile,
} from "@/utils/hyperpayKycUpload";
import {
  SPIKEX_CARD_APPLY_PHASES,
  SPIKEX_CARD_UNLOCK_LEVEL,
  HYPERPAY_PAY_CURRENCIES,
  HYPERPAY_APPLY_NAME_MAX_LEN,
  isValidHyperpayApplyName,
  formatApplyFreezeAmountLabel,
  buildApplyPrecheckInsufficientTipValuesMulti,
  fetchApplyPrecheckForCurrencies,
  resolveCanProceedWithApplyPrechecks,
  buildLevelDisplay,
  buildMockApplyStatus,
  buildMockCardBalance,
  buildMockCardSensitive,
  buildMockEligibility,
  buildMockEntryStatus,
  buildMockRecentTransactions,
  buildMockVipProgress,
  buildStatusDisplayContext,
  formatApplyReference,
  formatCardIssuedFee,
  formatUsdtAmount,
  isSpikexCardIssuedFeeWaived,
  SPIKEX_CARD_ORIGINAL_ISSUED_FEE_USDT,
  formatCardInfoMonthlyTransactionCap,
  formatFxFeeRange,
  formatRatePercent,
  formatUsdCapAmount,
  formatUsdFee,
  isCardManagementPhase,
  normalizeVipProgress,
  parseSpikexCardMockApplyStatus,
  parseSpikexCardMockEntryStatus,
  parseSpikexCardMockLevel,
  resolveEntryStatusUi,
  resolveMockEntryStatusFromLevel,
  resolvePostApplyView,
  resolveSpikexCardApplyPhase,
  normalizeHyperpayCardBalance,
  resolveDisplayCardBalance,
  resolveSpikexCardApplyEligibility,
  resolveSpikexCardDisplayUnlockLevel,
  resolveSpikexCardPageStatus,
  resolveSpikexCardUnlocked,
  resolveCardInfoTopUpFeeRate,
  fetchRecentHyperpayTransactions,
  trimApplyFormFields,
} from "@/utils/spikexCard";
import {
  isLoginStatusPending,
  navigateAccountsLogin,
  navigateSpikexCardDeposit,
  pushSpikexCardRoute,
} from "@/utils/spikexCardNav";
import {
  clearSpikexCardPageCache,
  getSpikexCardPageCacheUserKey,
  readSpikexCardPageCache,
  writeSpikexCardPageCache,
} from "@/utils/spikexCardPageCache";
import { useAppWebviewReuse } from "@/utils/appWebviewReuse";
import { toVerification } from "@/utils/native";

const { XtContext } = Context;
const { useTranslation } = Hooks;

const BENEFIT_ICONS = [benefitGlobe, benefitCard, benefitUsd];
const SUPPORTED_IMAGE_TYPES = ["image/png", "image/jpg", "image/jpeg"];
const KYC_APPROVED_STATUS = 2;
const KYC_REVIEWING_STATUS = 1;
const APPLY_STEPS = [
  {
    titleKey: "spikexCard.applyStep1Title",
    durationKey: "spikexCard.applyStep1Duration",
    descKey: "spikexCard.applyStep1Desc",
  },
  {
    titleKey: "spikexCard.applyStep2Title",
    durationKey: "spikexCard.applyStep2Duration",
    descKey: "spikexCard.applyStep2Desc",
  },
  {
    titleKey: "spikexCard.applyStep3Title",
    durationKey: "spikexCard.applyStep3Duration",
    descKey: "spikexCard.applyStep3Desc",
  },
];

const FORM_STEPS = [
  "spikexCard.formStepBasic",
  "spikexCard.formStepIdentity",
  "spikexCard.formStepAddress",
];

const DOC_TYPE_OPTIONS = [
  { value: 1, labelKey: "spikexCard.docTypePassport" },
  { value: 2, labelKey: "spikexCard.docTypeIdCard" },
  { value: 3, labelKey: "spikexCard.docTypeDriverLicense" },
];

const GENDER_OPTIONS = [
  { value: 1, labelKey: "spikexCard.genderMale" },
  { value: 2, labelKey: "spikexCard.genderFemale" },
];

const POA_OPTIONS = [
  { value: 1, labelKey: "spikexCard.poaTypeSelf" },
  { value: 2, labelKey: "spikexCard.poaTypeAssisted" },
];

const ATTACHMENT_TYPE_OPTIONS = [
  { value: 1, labelKey: "spikexCard.attachmentTypePoa" },
  { value: 2, labelKey: "spikexCard.attachmentTypeAml" },
];

function buildEmptyFormState() {
  return {
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    mobileCode: "",
    birthday: "",
    gender: "",
    countryId: "",
    nationalityId: "",
    docType: "",
    docNo: "",
    docNeverExpire: 0,
    docExpireDate: "",
    emergencyContact: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    poaType: "",
    frontDoc: null,
    backDoc: null,
    mixDoc: null,
    signImg: null,
    poaDoc: null,
  };
}

function resolveKycGateStatus(authInfo, userInfo) {
  const basicStatus = Number(authInfo?.userAuthInfoVo?.status || 0);
  const advancedStatus = Number(authInfo?.userAuthInfoVo?.advanceAuthStatus || 0);
  const authLevel = Number(userInfo?.authLevel || 0);

  if (authLevel >= 1 || basicStatus === 2 || advancedStatus === 2) {
    return KYC_APPROVED_STATUS;
  }
  if (basicStatus === 1 || advancedStatus === 1) {
    return KYC_REVIEWING_STATUS;
  }
  return 0;
}

function mapKycCardType(cardType) {
  if (cardType === 1) return 2;
  if (cardType === 2) return 1;
  if (cardType === 3) return 3;
  return "";
}

function formatBirthday(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(Number(value));
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeCountryCode(value) {
  return value ? String(value).trim().toUpperCase() : "";
}

function splitRealName(realName) {
  const name = String(realName || "").trim();
  if (!name) return { firstName: "", lastName: "" };
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return {
      firstName: parts.slice(0, -1).join(" "),
      lastName: parts[parts.length - 1],
    };
  }
  return { firstName: name, lastName: "" };
}

function buildPrefillFormState({ userInfo, authInfo, kycInfo }) {
  const next = buildEmptyFormState();
  const userAuthInfo = authInfo?.userAuthInfoVo || {};
  const splitName = splitRealName(kycInfo?.realName || userAuthInfo.realName || userInfo?.realName);

  next.firstName = splitName.firstName;
  next.lastName = splitName.lastName;
  next.email = String(userInfo?.email || "");
  next.mobile = String(userInfo?.mobile || "");
  next.mobileCode = String(userInfo?.countryCode || "").replace(/^\+/, "");
  next.birthday = formatBirthday(userAuthInfo.birthday);
  next.gender = "";
  next.countryId = normalizeCountryCode(authInfo?.country?.shortName || kycInfo?.countryId);
  next.nationalityId = normalizeCountryCode(authInfo?.country?.shortName || kycInfo?.countryId);
  next.docType = mapKycCardType(userAuthInfo.cardType || kycInfo?.cardType);
  next.docNo = String(kycInfo?.cardId || "");
  next.emergencyContact = splitName.firstName || splitName.lastName || "";

  return next;
}

function validateEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function toRawBase64(result) {
  const raw = String(result || "");
  const idx = raw.indexOf(",");
  return idx >= 0 ? raw.slice(idx + 1) : raw;
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(toRawBase64(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function mapInlineUploadField(field) {
  if (field === "signImg") return "sign_img";
  if (field === "frontDoc") return "front_doc";
  if (field === "backDoc") return "back_doc";
  if (field === "mixDoc") return "mix_doc";
  if (field === "poaDoc") return "poa_doc";
  return field;
}

function getImageLimit(field) {
  return getHyperpayKycImageLimit(mapInlineUploadField(field));
}

async function buildUploadFieldValue(file, field) {
  validateHyperpayKycImageFile(file, mapInlineUploadField(field));
  const uploadPath = await uploadHyperpayKycImage(file, undefined, { field: mapInlineUploadField(field) });
  return {
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type,
    uploadPath,
  };
}

async function buildAttachmentUploadValue(file, attachmentType) {
  const maxSize = 2 * 1024 * 1024;
  const isPdf = file.type === "application/pdf";
  const isImage = SUPPORTED_IMAGE_TYPES.includes(file.type);

  if (Number(attachmentType) === 2 && !isPdf) {
    throw new Error("formatPdf");
  }
  if (Number(attachmentType) === 1 && !isImage && !isPdf) {
    throw new Error("formatAttachment");
  }
  if (!isImage && !isPdf) {
    throw new Error("formatAttachment");
  }
  if (file.size > maxSize) {
    throw new Error("size");
  }

  const base64 = await readFileAsBase64(file);
  return {
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type,
    base64,
  };
}

function buildFeeCards(cardInfo) {
  const isVipBenefit = cardInfo?.cardIssuedFeeIsVipBenefit === true;
  const issuedFeeWaived = isSpikexCardIssuedFeeWaived(cardInfo, null);
  const issuedValue = issuedFeeWaived
    ? null
    : formatCardIssuedFee(cardInfo) ?? "—";
  const issuedOriginalValue = issuedFeeWaived
    ? `${formatUsdtAmount(SPIKEX_CARD_ORIGINAL_ISSUED_FEE_USDT)}U`
    : null;
  const monthlyCap = formatCardInfoMonthlyTransactionCap(cardInfo);
  const fxFeeValue = formatFxFeeRange(cardInfo?.fxFeeMin, cardInfo?.fxFeeMax);

  const managementRows = [
    {
      labelKey: "spikexCard.feeCardIssued",
      value: issuedValue,
      valueKey: issuedFeeWaived ? "spikexCard.issuedFeeFreeLabel" : null,
      originalValue: issuedOriginalValue,
      valueHighlight: isVipBenefit || issuedFeeWaived,
      badgeKey: isVipBenefit ? "spikexCard.feeVipBadge" : null,
    },
    {
      labelKey: "spikexCard.feeTopUp",
      value: formatRatePercent(resolveCardInfoTopUpFeeRate(cardInfo)) ?? "—",
      muted: true,
    },
    {
      labelKey: "spikexCard.feePerTransaction",
      value: formatUsdFee(cardInfo?.perTransactionFee) ?? "—",
      muted: true,
    },
    {
      labelKey: "spikexCard.feeAuth",
      value: formatUsdFee(cardInfo?.perTransactionAuthorizationFee) ?? "—",
      muted: true,
    },
    {
      labelKey: "spikexCard.feeFailed",
      value: formatUsdFee(cardInfo?.perFailedTransactionServiceFee) ?? "—",
      muted: true,
    },
  ];

  if (fxFeeValue) {
    managementRows.push({
      labelKey: "spikexCard.feeFx",
      value: fxFeeValue,
      muted: true,
    });
  }

  return [
    {
      titleKey: "spikexCard.feeManagementTitle",
      subtitleKey: "spikexCard.feeNoHidden",
      rows: managementRows,
    },
    {
      titleKey: "spikexCard.feeFeaturesTitle",
      featureRows: [
        "spikexCard.feeCardCurrency",
        "spikexCard.feeIssuedCountry",
        "spikexCard.feeAppleGooglePay",
        "spikexCard.feeOfflinePayment",
        "spikexCard.feeTransactionLimit",
      ],
    },
    {
      titleKey: "spikexCard.feeTransactionRuleTitle",
      rows: [
        {
          labelKey: "spikexCard.feePerTransactionCap",
          value: formatUsdCapAmount(cardInfo?.perTransactionCap ?? cardInfo?.per_transaction_cap) ?? "—",
        },
        {
          labelKey: "spikexCard.feeDailyCap",
          value: formatUsdCapAmount(cardInfo?.dailyTransactionCap ?? cardInfo?.daily_transaction_cap) ?? "—",
          muted: true,
        },
        {
          labelKey: "spikexCard.feeTransactionCap",
          ...(monthlyCap?.valueKey
            ? { valueKey: monthlyCap.valueKey }
            : { value: monthlyCap?.value ?? "—" }),
          muted: true,
        },
        {
          labelKey: "spikexCard.feeTopUpCap",
          value: formatUsdCapAmount(cardInfo?.perTopUpCap ?? cardInfo?.per_top_up_cap) ?? "—",
          muted: true,
        },
      ],
    },
  ];
}

const { getUrl } = Util;

const PcLockIcon = () => (
  <svg className={styles.unlockIconPc} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 118 0v3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PcChevronLeftIcon = () => (
  <svg className={styles.pcIcon16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PcArrowRightIcon = () => (
  <svg className={styles.pcIcon16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PcSparklesIcon = () => (
  <svg className={styles.pcIcon14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <path d="M9.94 2.94l.56 2.12 2.12.56-2.12.56-.56 2.12-.56-2.12-2.12-.56 2.12-.56.56-2.12zM4 14l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3zM18 8l.75 2.25L21 11l-2.25.75L18 14l-.75-2.25L15 11l2.25-.75L18 8z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PcGlobeIcon = () => (
  <svg className={styles.pcIcon14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
  </svg>
);

const PcCreditCardIcon = () => (
  <svg className={styles.pcIcon14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </svg>
);

const PcDollarIcon = () => (
  <svg className={styles.pcIcon14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <path d="M12 2v20M17 6H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PcZapIcon = () => (
  <svg className={styles.pcIcon14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockV6Icon = () => (
  <svg
    className={styles.lockV6Icon}
    xmlns="http://www.w3.org/2000/svg"
    width="9"
    height="10"
    viewBox="0 0 9 10"
    fill="none"
    aria-hidden
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.2 4.2V3C1.2 1.34425 2.54426 0 4.2 0C5.85575 0 7.2 1.34425 7.2 3V4.2C7.86274 4.2 8.4 4.73726 8.4 5.4V8.4C8.4 9.06274 7.86274 9.6 7.2 9.6H1.2C0.537258 9.6 0 9.06274 0 8.4V5.4C0 4.73726 0.537258 4.2 1.2 4.2ZM6 3V4.2H2.4V3C2.4 2.00655 3.20655 1.2 4.2 1.2C5.19345 1.2 6 2.00655 6 3Z"
      fill="#EAB308"
    />
  </svg>
);

const ProgressDotIcon = ({ style }) => (
  <svg
    className={styles.progressDot}
    style={style}
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    aria-hidden
  >
    <circle cx="5" cy="5" r="3" fill="#EBFFDB" />
    <circle cx="5" cy="5" r="4" stroke="#B9F78A" strokeOpacity="0.55" strokeWidth="2" />
  </svg>
);

const QualificationCheckIcon = () => (
  <svg
    className={styles.qualificationCheckIcon}
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="11"
    viewBox="0 0 14 11"
    fill="none"
    aria-hidden
  >
    <path
      d="M1 5.5L4.5 9L13 1"
      stroke="#B9F78A"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function ApplyStatusIcon({ variant }) {
  if (variant === "warning") {
    return <img className={styles.applyStatusIconImg} src={iconAttachmentRequired} alt="" aria-hidden />;
  }
  if (variant === "error") {
    return <img className={styles.applyStatusIconImg} src={iconRejected} alt="" aria-hidden />;
  }
  return <QualificationCheckIcon />;
}

function ApplyStatusCard({ t, entryStatus, applyStatus, statusUi, statusContext }) {
  if (!statusUi) return null;

  const applyRef = formatApplyReference(entryStatus, applyStatus);
  const rejectReason = statusContext?.rejectReason;
  const attachmentHint = statusContext?.attachmentHint;
  const balance = statusContext?.balance;
  const descLines = [...(statusUi.descKeys || [])];

  if (statusUi.showRejectReason && rejectReason) {
    descLines.unshift("spikexCard.statusFailReason");
  }

  return (
    <section className={styles.applyStatusCard}>
      <div className={styles.applyStatusMain}>
        <div
          className={`${styles.applyStatusIconWrap} ${
            statusUi.icon === "warning"
              ? styles.applyStatusIconWrapWarning
              : statusUi.icon === "error"
                ? styles.applyStatusIconWrapError
                : ""
          }`}
        >
          <ApplyStatusIcon variant={statusUi.icon} />
        </div>
        <div className={styles.applyStatusText}>
          <div className={styles.applyStatusTitle}>{t(statusUi.titleKey)}</div>
          {attachmentHint ? (
            <div className={styles.applyStatusDesc}>{attachmentHint}</div>
          ) : (
            descLines.map((descKey) => (
              <div key={descKey} className={styles.applyStatusDesc}>
                {descKey === "spikexCard.statusFailReason"
                  ? t(descKey, { reason: rejectReason })
                  : t(descKey)}
              </div>
            ))
          )}
          {statusUi.showBalance && balance ? (
            <div className={styles.applyStatusDesc}>
              {t("spikexCard.statusActiveBalance", { balance: `$${balance}` })}
            </div>
          ) : null}
          {statusUi.showApplyId && applyRef ? (
            <div className={styles.applyStatusDesc}>{t("spikexCard.statusApplyId", { id: applyRef })}</div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function AttachmentUploadSection({ t, attachmentType, attachmentFile, onTypeChange, onFileChange }) {
  const inputRef = useRef(null);
  const accept = Number(attachmentType) === 2 ? ".pdf" : ".jpg,.jpeg,.png,.pdf";

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const next = await buildAttachmentUploadValue(file, attachmentType);
      onFileChange(next);
    } catch (error) {
      if (error?.message === "formatPdf") {
        message.error(t("spikexCard.attachmentPdfOnlyTip"));
      } else if (error?.message === "formatAttachment") {
        message.error(t("spikexCard.attachmentFormatTip"));
      } else {
        message.error(t("spikexCard.formImageSizeTip"));
      }
    } finally {
      event.target.value = "";
    }
  };

  return (
    <section className={styles.attachmentCard}>
      <div className={styles.attachmentField}>
        <div className={styles.formFieldHeader}>
          <span className={styles.formFieldLabel}>
            {t("spikexCard.attachmentTypeLabel")}
            <span className={styles.formRequired}>*</span>
          </span>
        </div>
        <Select
          className={styles.attachmentSelect}
          value={attachmentType}
          onChange={(value) => {
            onTypeChange(value);
            onFileChange(null);
          }}
          dropdownClassName={styles.attachmentSelectDropdown}
          getPopupContainer={() => document.body}
          popupMatchSelectWidth
        >
          {ATTACHMENT_TYPE_OPTIONS.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </Select.Option>
          ))}
        </Select>
      </div>
      <FormField label={t("spikexCard.attachmentFileLabel")} required hint={t("spikexCard.attachmentFileHint")}>
        <div className={styles.fileField}>
          <button type="button" className={styles.fileBtn} onClick={() => inputRef.current?.click()}>
            {attachmentFile ? t("spikexCard.formReplaceFile") : t("spikexCard.formUploadFile")}
          </button>
          <div className={styles.fileMeta}>
            <span className={styles.fileName}>
              {attachmentFile?.fileName || t("spikexCard.formNoFileSelected")}
            </span>
          </div>
        </div>
        <input
          ref={inputRef}
          className={styles.fileInput}
          type="file"
          accept={accept}
          onChange={handleFileChange}
        />
      </FormField>
    </section>
  );
}

const LockUnlockBadgeIcon = () => (
  <svg className={styles.unlockIcon} viewBox="0 0 14 18" fill="none" aria-hidden>
    <path
      d="M1.71429 18C1.24286 18 0.839286 17.8321 0.503571 17.4964C0.167857 17.1607 0 16.7571 0 16.2857V7.71429C0 7.24286 0.167857 6.83929 0.503571 6.50357C0.839286 6.16786 1.24286 6 1.71429 6H2.57143V4.28571C2.57143 3.1 2.98929 2.08929 3.825 1.25357C4.66071 0.417857 5.67143 0 6.85714 0C8.04286 0 9.05357 0.417857 9.88929 1.25357C10.725 2.08929 11.1429 3.1 11.1429 4.28571V6H12C12.4714 6 12.875 6.16786 13.2107 6.50357C13.5464 6.83929 13.7143 7.24286 13.7143 7.71429V16.2857C13.7143 16.7571 13.5464 17.1607 13.2107 17.4964C12.875 17.8321 12.4714 18 12 18H1.71429ZM6.85714 13.7143C7.32857 13.7143 7.73214 13.5464 8.06786 13.2107C8.40357 12.875 8.57143 12.4714 8.57143 12C8.57143 11.5286 8.40357 11.125 8.06786 10.7893C7.73214 10.4536 7.32857 10.2857 6.85714 10.2857C6.38571 10.2857 5.98214 10.4536 5.64643 10.7893C5.31071 11.125 5.14286 11.5286 5.14286 12C5.14286 12.4714 5.31071 12.875 5.64643 13.2107C5.98214 13.5464 6.38571 13.7143 6.85714 13.7143ZM4.28571 6H9.42857V4.28571C9.42857 3.57143 9.17857 2.96429 8.67857 2.46429C8.17857 1.96429 7.57143 1.71429 6.85714 1.71429C6.14286 1.71429 5.53571 1.96429 5.03571 2.46429C4.53571 2.96429 4.28571 3.57143 4.28571 4.28571V6Z"
      fill="currentColor"
    />
  </svg>
);

function CardVisual({ t, unlockLevel, isUnlocked, spikexPc = false }) {
  return (
    <section className={styles.cardVisual} aria-label={t("spikexCard.title")}>
      {/* 纯色铺满；Frame 素材自带外圈留白无法直接撑满，Mastercard 单独叠层 */}
      <div className={styles.cardBg} aria-hidden />
      <img className={styles.mastercardMark} src={mastercardMark} alt="" />
      {!isUnlocked ? (
        <div className={cx(styles.unlockBadge, spikexPc && styles.unlockBadgePc)}>
          {spikexPc ? <PcLockIcon /> : <LockUnlockBadgeIcon />}
          <span className={styles.unlockText}>{t("spikexCard.unlockAtLevel", { level: unlockLevel })}</span>
        </div>
      ) : null}
      <img className={styles.corporateLogo} src={corporateLogo} alt="" />
      <div className={styles.memberBlock}>
        <span className={styles.memberLabel}>{t("spikexCard.member")}</span>
        <div className={styles.memberDividers}>
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className={styles.memberDivider} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LevelSection({ t, levelData, showCurrentLabel = true }) {
  const showPrev = levelData.prevLevel < levelData.currentLevel;
  const showNext = levelData.nextLevel > levelData.currentLevel;

  return (
    <section className={styles.levelSection} aria-label="level">
      <div className={styles.levelPartnerTrack}>
        <div className={styles.levelPartnerTrackInner}>
          <img src={affiliateArcDivider} alt="" className={styles.levelPartnerArcImg} />
          <div className={styles.levelPartnerLabelsRow}>
            {showPrev ? (
              <div
                className={`${styles.levelPartnerLabelSlot} ${styles.levelPartnerNodeMuted}`}
                style={{ left: `${50 - ((117 - 21) / 264) * 100}%` }}
              >
                <span className={styles.levelPartnerNodeLv}>Lv.{levelData.prevLevel}</span>
              </div>
            ) : null}
            <div className={`${styles.levelPartnerLabelSlot} ${styles.levelPartnerNodeSlotActive}`} style={{ left: "50%" }}>
              <span className={styles.levelPartnerNodeLv}>Lv.{levelData.currentLevel}</span>
              {showCurrentLabel ? <span className={styles.levelPartnerNodeSub}>{t("spikexCard.currentLevel")}</span> : null}
            </div>
            {showNext ? (
              <div
                className={`${styles.levelPartnerLabelSlot} ${styles.levelPartnerNodeMuted}`}
                style={{ left: `${50 + ((211 - 117) / 264) * 100}%` }}
              >
                <span className={styles.levelPartnerNodeLv}>Lv.{levelData.nextLevel}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function PcBackBar({ t }) {
  return (
    <a href={getUrl("/accounts/user/overview")} className={styles.pcBackLink}>
      <PcChevronLeftIcon />
      {t("spikexCard.backToAccount")}
    </a>
  );
}

function BenefitsPromoPanel({ t }) {
  const perks = [
    { Icon: PcGlobeIcon, labelKey: "spikexCard.perkCountries" },
    { Icon: PcCreditCardIcon, labelKey: "spikexCard.perkMerchants" },
    { Icon: PcDollarIcon, labelKey: "spikexCard.perkUsd" },
    { Icon: PcZapIcon, labelKey: "spikexCard.perkTopUp" },
  ];

  return (
    <aside className={styles.pcPromoPanel}>
      <div className={styles.pcPromoGlow} aria-hidden />
      <div className={styles.pcPromoInner}>
        <div className={styles.pcPromoBadge}>{t("spikexCard.limitedBeta")}</div>
        <h2 className={styles.pcPromoHeadline}>
          {t("spikexCard.promoHeadlineLine1")}
          <br />
          {t("spikexCard.promoHeadlineLine2")}
        </h2>
        <p className={styles.pcPromoDesc}>{t("spikexCard.promoDesc")}</p>
        <div className={styles.pcPromoDivider} />
        <div className={styles.pcPerksLabelRow}>
          <PcSparklesIcon />
          <span>{t("spikexCard.includedPerks")}</span>
        </div>
        <div className={styles.pcPerksGrid}>
          {perks.map(({ Icon, labelKey }) => (
            <div key={labelKey} className={styles.pcPerkItem}>
              <Icon />
              <span>{t(labelKey)}</span>
            </div>
          ))}
        </div>
        <p className={styles.pcPromoFinePrint}>{t("spikexCard.promoFinePrint")}</p>
      </div>
    </aside>
  );
}

function ProgressSection({ t, levelData, onViewUpgradePath, showRequirementsTitle = false }) {
  const nextLevel = levelData.nextLevel > levelData.currentLevel ? levelData.nextLevel : null;

  return (
    <section className={styles.progressCard}>
      <div className={styles.progressRow}>
        <span className={styles.levelTag}>V{levelData.currentLevel}</span>
        <div className={styles.progressBarWrap}>
          <div className={styles.progressBarBg}>
            <div className={styles.progressBarFill} style={{ width: `${levelData.progressPercent}%` }} />
          </div>
          <ProgressDotIcon style={{ left: `${levelData.progressPercent}%` }} />
        </div>
        {nextLevel ? (
          <span className={`${styles.levelTag} ${styles.levelTagNext}`}>
            <LockV6Icon />
            V{nextLevel}
          </span>
        ) : null}
      </div>
      {showRequirementsTitle && nextLevel ? (
        <p className={styles.pcRequirementsTitle}>
          {t("spikexCard.requirementsTitle", { level: nextLevel })}
        </p>
      ) : null}
      <div className={styles.progressHints}>
        {levelData.showTradeProgress ? (
          <p className={styles.progressHint}>
            {t("spikexCard.progressTradePrefix")}
            <span className={styles.progressAmount}>${levelData.tradeRemainUsd}</span>
            {t("spikexCard.progressTradeSuffix")}
          </p>
        ) : null}
        {levelData.showInviteProgress ? (
          <p className={styles.progressHint}>
            {t("spikexCard.progressInvitePrefix")}
            <span className={styles.progressAmount}>{levelData.inviteRemainUsers}</span>
            {t("spikexCard.progressInviteSuffix")}
          </p>
        ) : null}
      </div>
      <button type="button" className={styles.upgradeBtn} onClick={onViewUpgradePath}>
        {t("spikexCard.viewUpgradePath")}
        <PcArrowRightIcon />
      </button>
    </section>
  );
}

function UnlockStateContent({ t, levelData, onViewUpgradePath }) {
  const benefits = [
    { icon: BENEFIT_ICONS[0], title: t("spikexCard.benefit1Title"), desc: t("spikexCard.benefit1Desc") },
    { icon: BENEFIT_ICONS[1], title: t("spikexCard.benefit2Title"), desc: t("spikexCard.benefit2Desc") },
    { icon: BENEFIT_ICONS[2], title: t("spikexCard.benefit3Title"), desc: t("spikexCard.benefit3Desc") },
  ];

  return (
    <>
      <LevelSection t={t} levelData={levelData} />
      <ProgressSection t={t} levelData={levelData} onViewUpgradePath={onViewUpgradePath} />
      <section className={styles.benefitsSection}>
        <h2 className={styles.benefitsTitle}>{t("spikexCard.benefitsTitle")}</h2>
        <div className={styles.benefitsCard}>
          {benefits.map((item, index) => (
            <React.Fragment key={item.title}>
              {index > 0 ? <div className={styles.benefitDivider} /> : null}
              <div className={styles.benefitItem}>
                <div className={styles.benefitIconWrap}>
                  <img className={styles.benefitIcon} src={item.icon} alt="" />
                </div>
                <div className={styles.benefitText}>
                  <div className={styles.benefitName}>{item.title}</div>
                  <div className={styles.benefitDesc}>{item.desc}</div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </section>
    </>
  );
}

function FeeCard({ t, card }) {
  return (
    <article className={styles.feeCard}>
      <div className={styles.feeCardHeader}>
        <h3 className={styles.feeCardTitle}>{t(card.titleKey)}</h3>
        {card.subtitleKey ? <span className={styles.feeCardSubtitle}>{t(card.subtitleKey)}</span> : null}
      </div>
      <div className={styles.feeCardBody}>
        {card.rows
          ? card.rows.map((row, index) => (
              <React.Fragment key={row.labelKey}>
                {index > 0 ? <div className={styles.feeRowDivider} /> : null}
                <div className={styles.feeRow}>
                  <div className={styles.feeRowLabelWrap}>
                    <span className={`${styles.feeRowLabel} ${row.muted ? styles.feeRowLabelMuted : ""}`}>{t(row.labelKey)}</span>
                    {row.badgeKey ? <span className={styles.feeVipBadge}>{t(row.badgeKey)}</span> : null}
                  </div>
                  {row.value || row.valueKey || row.originalValue ? (
                    <span className={`${styles.feeRowValue} ${row.valueHighlight ? styles.feeRowValueHighlight : ""} ${row.muted ? styles.feeRowLabelMuted : ""}`}>
                      {row.originalValue ? (
                        <span className={styles.feeOriginalValue}>{row.originalValue}</span>
                      ) : null}
                      {row.valueKey ? t(row.valueKey) : row.value}
                    </span>
                  ) : null}
                </div>
              </React.Fragment>
            ))
          : null}
        {card.featureRows
          ? card.featureRows.map((row, index) => (
              <React.Fragment key={row}>
                {index > 0 ? <div className={styles.feeRowDivider} /> : null}
                <div className={styles.feeFeatureRow}>
                  <span className={styles.feeFeatureText}>{row.startsWith("spikexCard.") ? t(row) : row}</span>
                </div>
              </React.Fragment>
            ))
          : null}
      </div>
    </article>
  );
}

const IssuedFeePromoIcon = () => (
  <svg className={styles.issuedFeePromoIcon} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M9.94 2.94l.56 2.12 2.12.56-2.12.56-.56 2.12-.56-2.12-2.12-.56 2.12-.56.56-2.12z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 14l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3zM18 8l.75 2.25L21 11l-2.25.75L18 14l-.75-2.25L15 11l2.25-.75L18 8z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function IssuedFeePromoBanner({ t }) {
  return (
    <div className={styles.issuedFeePromo}>
      <div className={styles.issuedFeePromoGlow} aria-hidden />
      <div className={styles.issuedFeePromoInner}>
        <div className={styles.issuedFeePromoIconWrap}>
          <IssuedFeePromoIcon />
        </div>
        <p className={styles.issuedFeePromoText}>{t("spikexCard.issuedFeePromo")}</p>
        <span className={styles.issuedFeePromoTag}>{t("spikexCard.issuedFeeFreeLabel")}</span>
      </div>
    </div>
  );
}

function FeeSection({ t, feeCards, stack = false }) {
  const feeCarouselViewportRef = useRef(null);
  const showIssuedFeePromo = feeCards.some((card) =>
    card.rows?.some((row) => row.originalValue)
  );

  useEffect(() => {
    if (stack) return undefined;

    const viewport = feeCarouselViewportRef.current;
    if (!viewport) return undefined;

    let isDragging = false;
    let startX = 0;
    let scrollStart = 0;
    let hasDragged = false;

    const stopDragging = () => {
      if (!isDragging) return;
      isDragging = false;
      viewport.classList.remove(styles.feeCarouselDragging);
    };

    const handleMouseDown = (event) => {
      if (event.button !== 0 || viewport.scrollWidth <= viewport.clientWidth) return;

      isDragging = true;
      hasDragged = false;
      startX = event.pageX;
      scrollStart = viewport.scrollLeft;
      viewport.classList.add(styles.feeCarouselDragging);
    };

    const handleMouseMove = (event) => {
      if (!isDragging) return;

      const deltaX = event.pageX - startX;
      if (Math.abs(deltaX) > 3) hasDragged = true;
      viewport.scrollLeft = scrollStart - deltaX;
    };

    const handleClick = (event) => {
      if (!hasDragged) return;
      event.preventDefault();
      event.stopPropagation();
      hasDragged = false;
    };

    viewport.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", stopDragging);
    viewport.addEventListener("click", handleClick, true);

    return () => {
      viewport.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopDragging);
      viewport.removeEventListener("click", handleClick, true);
      viewport.classList.remove(styles.feeCarouselDragging);
    };
  }, [stack, feeCards]);

  return (
    <section className={cx(styles.feeSection, stack && styles.feeSectionStack)}>
      <h2 className={styles.feeSectionTitle}>{t("spikexCard.cardFeesTitle")}</h2>
      {showIssuedFeePromo ? <IssuedFeePromoBanner t={t} /> : null}
      {stack ? (
        <div className={styles.feeCardStack}>
          {feeCards.map((card) => (
            <FeeCard key={card.titleKey} t={t} card={card} />
          ))}
        </div>
      ) : (
        <div className={styles.feeCarouselViewport} ref={feeCarouselViewportRef}>
          <div className={styles.feeCarousel}>
            {feeCards.map((card, index) => (
              <div
                key={card.titleKey}
                className={cx(
                  styles.feeCardSnapItem,
                  index === 0 && styles.feeCardSnapItemFirst,
                  index === feeCards.length - 1 && styles.feeCardSnapItemLast
                )}
              >
                <FeeCard t={t} card={card} />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function QualificationSection({ t, levelData }) {
  return (
    <section className={styles.qualificationCard}>
      <div className={styles.qualificationMain}>
        <div className={styles.qualificationIconWrap}>
          <QualificationCheckIcon />
        </div>
        <div className={styles.qualificationText}>
          <div className={styles.qualificationTitle}>{t("spikexCard.qualificationConfirmed")}</div>
          {levelData.unlockYear ? (
            <div className={styles.qualificationDate}>
              {t("spikexCard.unlockedAt", {
                year: levelData.unlockYear,
                month: levelData.unlockMonth,
                day: levelData.unlockDay,
              })}
            </div>
          ) : null}
        </div>
      </div>
      <span className={styles.qualificationLevelBadge}>Lv.{levelData.currentLevel}</span>
    </section>
  );
}

function ApplyProcessSection({ t, cardInfo }) {
  return (
    <section className={styles.applyProcessCard}>
      <div className={styles.applyProcessHeader}>
        <h2 className={styles.applyProcessTitle}>{t("spikexCard.applyProcessTitle")}</h2>
        <span className={styles.applyProcessDuration}>{t("spikexCard.applyProcessDuration")}</span>
      </div>
      <div className={styles.applySteps}>
        <span className={styles.applyStepsLine} aria-hidden />
        {APPLY_STEPS.map((step, index) => (
          <div key={step.titleKey} className={styles.applyStep}>
            <div className={styles.applyStepIndex}>{index + 1}</div>
            <div className={styles.applyStepContent}>
              <div className={styles.applyStepTitleRow}>
                <span className={styles.applyStepTitle}>{t(step.titleKey)}</span>
                <span className={styles.applyStepDuration}>{t(step.durationKey)}</span>
              </div>
              <p className={styles.applyStepDesc}>
                {step.titleKey === "spikexCard.applyStep2Title"
                  ? (() => {
                      const amount = getFirstRechargeAmount(cardInfo);
                      return t(step.descKey, { amount: amount ? `${amount} USD` : "--" });
                    })()
                  : t(step.descKey)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PcEligibleApplyLayout({
  t,
  levelData,
  postApplyView,
  displayUnlockLevel,
  isUnlocked,
  feeCards,
  cardInfo,
  footerProps,
}) {
  return (
    <div className={styles.pcApplyGrid}>
      <div className={cx(styles.pcLeftPanel, styles.pcApplyLeftPanel)}>
        <div className={styles.pcCardVisualWrap}>
          <CardVisual
            t={t}
            unlockLevel={displayUnlockLevel}
            isUnlocked={isUnlocked}
            spikexPc
          />
        </div>
        {postApplyView.showLevel ? <LevelSection t={t} levelData={levelData} /> : null}
        <div className={styles.pcLeftDivider} />
        <div className={styles.pcApplyPanelBody}>
          {postApplyView.showQualification ? (
            <QualificationSection t={t} levelData={levelData} />
          ) : null}
          {postApplyView.showApplyProcess ? (
            <ApplyProcessSection t={t} cardInfo={cardInfo} />
          ) : null}
        </div>
        <div className={styles.pcApplyFeeBlock}>
          <FeeSection t={t} feeCards={feeCards} />
        </div>
        <div className={styles.pcApplyPanelAction}>
          <CardFooter {...footerProps} variant={postApplyView.footer} footerLabelKey={postApplyView.footerLabelKey} />
        </div>
      </div>
      <aside className={styles.pcApplyRightCol}>
        <BenefitsPromoPanel t={t} />
      </aside>
    </div>
  );
}

function PcInProgressApplyLayout({
  t,
  postApplyView,
  displayUnlockLevel,
  isUnlocked,
  entryStatus,
  applyStatus,
  statusUi,
  statusContext,
  attachmentType,
  attachmentFile,
  onAttachmentTypeChange,
  onAttachmentFileChange,
  feeCards,
  footerProps,
}) {
  return (
    <div className={styles.pcApplyGrid}>
      <div className={cx(styles.pcLeftPanel, styles.pcApplyInProgressPanel, styles.pcApplyLeftPanel)}>
        <div className={styles.pcCardVisualWrap}>
          <CardVisual
            t={t}
            unlockLevel={displayUnlockLevel}
            isUnlocked={isUnlocked}
            spikexPc
          />
        </div>
        <div className={styles.pcApplyInProgressBody}>
          {postApplyView.showStatusCard ? (
            <ApplyStatusCard
              t={t}
              entryStatus={entryStatus}
              applyStatus={applyStatus}
              statusUi={statusUi}
              statusContext={statusContext}
            />
          ) : null}
          {postApplyView.showAttachmentForm ? (
            <AttachmentUploadSection
              t={t}
              attachmentType={attachmentType}
              attachmentFile={attachmentFile}
              onTypeChange={onAttachmentTypeChange}
              onFileChange={onAttachmentFileChange}
            />
          ) : null}
        </div>
        <div className={styles.pcApplyFeeBlock}>
          <FeeSection t={t} feeCards={feeCards} />
        </div>
        {postApplyView.footer && postApplyView.footer !== "none" ? (
          <div className={styles.pcApplyPanelAction}>
            <CardFooter {...footerProps} variant={postApplyView.footer} footerLabelKey={postApplyView.footerLabelKey} />
          </div>
        ) : null}
      </div>
      <aside className={styles.pcApplyRightCol}>
        <BenefitsPromoPanel t={t} />
      </aside>
    </div>
  );
}

function PostApplyContent({
  t,
  levelData,
  postApplyView,
  entryStatus,
  applyStatus,
  statusUi,
  statusContext,
  feeCards,
  cardInfo,
  attachmentType,
  attachmentFile,
  onAttachmentTypeChange,
  onAttachmentFileChange,
}) {
  return (
    <div className={styles.applyContent}>
      {postApplyView.showLevel ? <LevelSection t={t} levelData={levelData} /> : null}
      {postApplyView.showQualification ? (
        <QualificationSection t={t} levelData={levelData} />
      ) : null}
      {postApplyView.showStatusCard ? (
        <ApplyStatusCard
          t={t}
          entryStatus={entryStatus}
          applyStatus={applyStatus}
          statusUi={statusUi}
          statusContext={statusContext}
        />
      ) : null}
      {postApplyView.showAttachmentForm ? (
        <AttachmentUploadSection
          t={t}
          attachmentType={attachmentType}
          attachmentFile={attachmentFile}
          onTypeChange={onAttachmentTypeChange}
          onFileChange={onAttachmentFileChange}
        />
      ) : null}
      {postApplyView.showApplyProcess ? (
        <ApplyProcessSection t={t} cardInfo={cardInfo} />
      ) : null}
      {!postApplyView.showManageView ? <FeeSection t={t} feeCards={feeCards} /> : null}
    </div>
  );
}

function CardFooter({
  t,
  variant,
  footerLabelKey,
  onApply,
  onReapply,
  onSubmitAttachment,
  onBindCard,
  onTopUp,
  busy,
}) {
  if (variant === "waitlist") {
    return (
      <div className={styles.applyFooter}>
        <button type="button" className={styles.waitlistBtn} disabled>
          <QualificationCheckIcon />
          <span>{t("spikexCard.addedToWaitlist")}</span>
        </button>
      </div>
    );
  }

  if (variant === "processing") {
    return (
      <div className={styles.applyFooter}>
        <button type="button" className={styles.waitlistBtn} disabled>
          {busy ? <Spin size="small" /> : null}
          <span>{t(footerLabelKey || "spikexCard.statusProcessingBtn")}</span>
        </button>
      </div>
    );
  }

  if (variant === "submitAttachment") {
    return (
      <div className={styles.applyFooter}>
        <button type="button" className={styles.applyBtn} onClick={onSubmitAttachment} disabled={busy}>
          {busy ? <Spin size="small" /> : t(footerLabelKey || "spikexCard.submitAttachment")}
        </button>
      </div>
    );
  }

  if (variant === "reapply") {
    return (
      <div className={styles.applyFooter}>
        <button type="button" className={styles.applyBtn} onClick={onReapply} disabled={busy}>
          {busy ? <Spin size="small" /> : t(footerLabelKey || "spikexCard.reapplyNow")}
        </button>
      </div>
    );
  }

  if (variant === "bindCard") {
    return (
      <div className={styles.applyFooter}>
        <button type="button" className={styles.applyBtn} onClick={onBindCard}>
          {t("spikexCard.bindCardNow")}
        </button>
      </div>
    );
  }

  if (variant === "topup") {
    return (
      <div className={styles.applyFooter}>
        <button type="button" className={styles.applyBtn} onClick={onTopUp}>
          {t("spikexCard.topUp")}
        </button>
      </div>
    );
  }

  if (variant === "none") {
    return null;
  }

  if (variant === "frozen") {
    return (
      <div className={styles.applyFooter}>
        <button type="button" className={styles.waitlistBtn} disabled>
          <span>{t(footerLabelKey || "spikexCard.statusFrozenBtn")}</span>
        </button>
      </div>
    );
  }

  return (
    <div className={styles.applyFooter}>
      <button type="button" className={styles.applyBtn} onClick={onApply} disabled={busy}>
        {busy ? <Spin size="small" /> : t("spikexCard.applyNow")}
      </button>
    </div>
  );
}

function FormField({ label, children, required = false, hint = "" }) {
  return (
    <label className={styles.formField}>
      <div className={styles.formFieldHeader}>
        <span className={styles.formFieldLabel}>
          {label}
          {required ? <span className={styles.formRequired}>*</span> : null}
        </span>
        {hint ? <span className={styles.formFieldHint}>{hint}</span> : null}
      </div>
      {children}
    </label>
  );
}

function InputField(props) {
  return <input {...props} className={styles.formInput} />;
}

function SelectField({ children, ...props }) {
  return (
    <select {...props} className={styles.formSelect}>
      {children}
    </select>
  );
}

function FileField({ t, label, value, required = false, hint = "", onChange, field }) {
  const inputRef = useRef(null);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const next = await buildUploadFieldValue(file, field);
      onChange(next);
    } catch (error) {
      message.error(t(resolveHyperpayKycImageErrorKey(error, mapInlineUploadField(field))));
    } finally {
      event.target.value = "";
    }
  };

  return (
    <FormField label={label} required={required} hint={hint}>
      <div className={styles.fileField}>
        <button type="button" className={styles.fileBtn} onClick={() => inputRef.current?.click()}>
          {value ? t("spikexCard.formReplaceFile") : t("spikexCard.formUploadFile")}
        </button>
        <div className={styles.fileMeta}>
          <span className={styles.fileName}>{value?.fileName || t("spikexCard.formNoFileSelected")}</span>
        </div>
      </div>
      <input ref={inputRef} className={styles.fileInput} type="file" accept=".jpg,.jpeg,.png" onChange={handleFileChange} />
    </FormField>
  );
}

function SpikexCardApplyModal({
  open,
  t,
  onClose,
  onSubmit,
  submitting,
  formState,
  setFormState,
  currentStep,
  setCurrentStep,
  needPoa,
  cardInfo,
  applyPrecheck,
  eligibility,
}) {
  const freezeAmountLabel = useMemo(
    () => formatApplyFreezeAmountLabel({ precheck: applyPrecheck, cardInfo, eligibility }),
    [applyPrecheck, cardInfo, eligibility]
  );
  const firstRechargeAmount = useMemo(() => getFirstRechargeAmount(cardInfo), [cardInfo]);

  const updateField = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const validateStep = useCallback(() => {
    if (currentStep === 0) {
      if (!isValidHyperpayApplyName(formState.firstName)) {
        return "spikexCard.formErrorFirstName";
      }
      if (!isValidHyperpayApplyName(formState.lastName)) {
        return "spikexCard.formErrorLastName";
      }
      if (!validateEmail(formState.email)) {
        return "spikexCard.formErrorEmail";
      }
      if (!formState.mobile || String(formState.mobile).trim().length < 6) {
        return "spikexCard.formErrorMobile";
      }
      if (!formState.mobileCode) {
        return "spikexCard.formErrorMobileCode";
      }
      if (!formState.birthday) {
        return "spikexCard.formErrorBirthday";
      }
      if (!formState.gender) {
        return "spikexCard.formErrorGender";
      }
      if (!formState.countryId || String(formState.countryId).trim().length !== 2) {
        return "spikexCard.formErrorCountryId";
      }
      if (!formState.nationalityId || String(formState.nationalityId).trim().length !== 2) {
        return "spikexCard.formErrorNationalityId";
      }
      if (!formState.docType) {
        return "spikexCard.formErrorDocType";
      }
      if (!formState.docNo) {
        return "spikexCard.formErrorDocNo";
      }
      if (formState.docNeverExpire !== 1 && !formState.docExpireDate) {
        return "spikexCard.formErrorDocExpireDate";
      }
      if (!isValidHyperpayApplyName(formState.emergencyContact)) {
        return "spikexCard.formErrorEmergencyContact";
      }
    }

    if (currentStep === 1) {
      if (!formState.frontDoc?.uploadPath) {
        return "spikexCard.formErrorFrontDoc";
      }
      if (Number(formState.docType) === 2 && !formState.backDoc?.uploadPath) {
        return "spikexCard.formErrorBackDoc";
      }
      if (!formState.mixDoc?.uploadPath) {
        return "spikexCard.formErrorMixDoc";
      }
      if (!formState.signImg?.uploadPath) {
        return "spikexCard.formErrorSignImg";
      }
    }

    if (currentStep === 2) {
      if (!formState.address) {
        return "spikexCard.formErrorAddress";
      }
      if (!formState.city) {
        return "spikexCard.formErrorCity";
      }
      if (!formState.state) {
        return "spikexCard.formErrorState";
      }
      if (!formState.zipCode) {
        return "spikexCard.formErrorZipCode";
      }
      if (needPoa && !formState.poaType) {
        return "spikexCard.formErrorPoaType";
      }
      if (needPoa && Number(formState.poaType) === 1 && !formState.poaDoc?.uploadPath) {
        return "spikexCard.formErrorPoaDoc";
      }
    }

    return "";
  }, [currentStep, formState, needPoa]);

  const handleNext = () => {
    const errorKey = validateStep();
    if (errorKey) {
      message.error(t(errorKey));
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, FORM_STEPS.length - 1));
  };

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = () => {
    const errorKey = validateStep();
    if (errorKey) {
      message.error(t(errorKey));
      return;
    }
    onSubmit();
  };

  if (!open) return null;

  return (
    <div className={styles.modalMask} onClick={onClose}>
      <div className={styles.modalCard} onClick={(event) => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h3 className={styles.modalTitle}>{t("spikexCard.formTitle")}</h3>
            <p className={styles.modalDesc}>{t("spikexCard.formDesc", { amount: freezeAmountLabel })}</p>
          </div>
          <button type="button" className={styles.modalCloseBtn} onClick={onClose} aria-label="close">
            ×
          </button>
        </div>

        <div className={styles.formSteps}>
          {FORM_STEPS.map((stepKey, index) => {
            const active = index === currentStep;
            const done = index < currentStep;
            return (
              <div key={stepKey} className={styles.formStepItem}>
                <div className={`${styles.formStepBadge} ${active ? styles.formStepBadgeActive : ""} ${done ? styles.formStepBadgeDone : ""}`}>
                  {index + 1}
                </div>
                <span className={`${styles.formStepLabel} ${active ? styles.formStepLabelActive : ""}`}>{t(stepKey)}</span>
              </div>
            );
          })}
        </div>

        <div className={styles.modalBody}>
          {currentStep === 0 ? (
            <div className={styles.formGrid}>
              <FormField label={t("spikexCard.formFirstName")} required>
                <InputField
                  value={formState.firstName}
                  maxLength={HYPERPAY_APPLY_NAME_MAX_LEN}
                  onChange={(e) => updateField("firstName", e.target.value)}
                  placeholder={t("spikexCard.formFirstNamePlaceholder")}
                />
              </FormField>
              <FormField label={t("spikexCard.formLastName")} required>
                <InputField
                  value={formState.lastName}
                  maxLength={HYPERPAY_APPLY_NAME_MAX_LEN}
                  onChange={(e) => updateField("lastName", e.target.value)}
                  placeholder={t("spikexCard.formLastNamePlaceholder")}
                />
              </FormField>
              <FormField label={t("spikexCard.formEmail")} required>
                <InputField type="email" value={formState.email} onChange={(e) => updateField("email", e.target.value)} placeholder="name@example.com" />
              </FormField>
              <FormField label={t("spikexCard.formMobileCode")} required>
                <InputField value={formState.mobileCode} onChange={(e) => updateField("mobileCode", e.target.value.replace(/[^\d]/g, ""))} placeholder="86" />
              </FormField>
              <FormField label={t("spikexCard.formMobile")} required>
                <InputField value={formState.mobile} onChange={(e) => updateField("mobile", e.target.value.replace(/[^\d]/g, ""))} placeholder={t("spikexCard.formMobilePlaceholder")} />
              </FormField>
              <FormField label={t("spikexCard.formBirthday")} required>
                <InputField type="date" value={formState.birthday} onChange={(e) => updateField("birthday", e.target.value)} />
              </FormField>
              <FormField label={t("spikexCard.formGender")} required>
                <SelectField value={formState.gender} onChange={(e) => updateField("gender", Number(e.target.value) || "")}>
                  <option value="">{t("spikexCard.formSelectPlaceholder")}</option>
                  {GENDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </SelectField>
              </FormField>
              <FormField label={t("spikexCard.formEmergencyContact")} required>
                <InputField
                  value={formState.emergencyContact}
                  maxLength={HYPERPAY_APPLY_NAME_MAX_LEN}
                  onChange={(e) => updateField("emergencyContact", e.target.value)}
                  placeholder={t("spikexCard.formEmergencyContactPlaceholder")}
                />
              </FormField>
              <FormField label={t("spikexCard.formCountryId")} required hint={t("spikexCard.formIsoHint")}>
                <InputField value={formState.countryId} onChange={(e) => updateField("countryId", normalizeCountryCode(e.target.value).slice(0, 2))} placeholder="US" />
              </FormField>
              <FormField label={t("spikexCard.formNationalityId")} required hint={t("spikexCard.formIsoHint")}>
                <InputField value={formState.nationalityId} onChange={(e) => updateField("nationalityId", normalizeCountryCode(e.target.value).slice(0, 2))} placeholder="CN" />
              </FormField>
              <FormField label={t("spikexCard.formDocType")} required>
                <SelectField value={formState.docType} onChange={(e) => updateField("docType", Number(e.target.value) || "")}>
                  <option value="">{t("spikexCard.formSelectPlaceholder")}</option>
                  {DOC_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </SelectField>
              </FormField>
              <FormField label={t("spikexCard.formDocNo")} required>
                <InputField value={formState.docNo} onChange={(e) => updateField("docNo", e.target.value)} placeholder={t("spikexCard.formDocNoPlaceholder")} />
              </FormField>
              <FormField label={t("spikexCard.formDocNeverExpire")} required>
                <SelectField value={formState.docNeverExpire} onChange={(e) => updateField("docNeverExpire", Number(e.target.value))}>
                  <option value={0}>{t("spikexCard.optionNo")}</option>
                  <option value={1}>{t("spikexCard.optionYes")}</option>
                </SelectField>
              </FormField>
              {Number(formState.docNeverExpire) !== 1 ? (
                <FormField label={t("spikexCard.formDocExpireDate")} required>
                  <InputField type="date" value={formState.docExpireDate} onChange={(e) => updateField("docExpireDate", e.target.value)} />
                </FormField>
              ) : null}
            </div>
          ) : null}

          {currentStep === 1 ? (
            <div className={styles.formGrid}>
              <FileField t={t} field="frontDoc" label={t("spikexCard.formFrontDoc")} required hint={t("spikexCard.formImageHint")} value={formState.frontDoc} onChange={(value) => updateField("frontDoc", value)} />
              {Number(formState.docType) === 2 ? (
                <FileField t={t} field="backDoc" label={t("spikexCard.formBackDoc")} required hint={t("spikexCard.formImageHint")} value={formState.backDoc} onChange={(value) => updateField("backDoc", value)} />
              ) : null}
              <FileField t={t} field="mixDoc" label={t("spikexCard.formMixDoc")} required hint={t("spikexCard.formImageHint")} value={formState.mixDoc} onChange={(value) => updateField("mixDoc", value)} />
              <FileField t={t} field="signImg" label={t("spikexCard.formSignImg")} required hint={t("spikexCard.formSignHint")} value={formState.signImg} onChange={(value) => updateField("signImg", value)} />
            </div>
          ) : null}

          {currentStep === 2 ? (
            <div className={styles.formGrid}>
              <FormField label={t("spikexCard.formAddress")} required>
                <InputField value={formState.address} onChange={(e) => updateField("address", e.target.value)} placeholder={t("spikexCard.formAddressPlaceholder")} />
              </FormField>
              <FormField label={t("spikexCard.formCity")} required>
                <InputField value={formState.city} onChange={(e) => updateField("city", e.target.value)} placeholder={t("spikexCard.formCityPlaceholder")} />
              </FormField>
              <FormField label={t("spikexCard.formState")} required>
                <InputField value={formState.state} onChange={(e) => updateField("state", e.target.value)} placeholder={t("spikexCard.formStatePlaceholder")} />
              </FormField>
              <FormField label={t("spikexCard.formZipCode")} required>
                <InputField value={formState.zipCode} onChange={(e) => updateField("zipCode", e.target.value)} placeholder={t("spikexCard.formZipCodePlaceholder")} />
              </FormField>
              {needPoa ? (
                <FormField label={t("spikexCard.formPoaType")} required>
                  <SelectField value={formState.poaType} onChange={(e) => updateField("poaType", Number(e.target.value) || "")}>
                    <option value="">{t("spikexCard.formSelectPlaceholder")}</option>
                    {POA_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </option>
                    ))}
                  </SelectField>
                </FormField>
              ) : null}
              {needPoa && Number(formState.poaType) === 1 ? (
                <FileField t={t} field="poaDoc" label={t("spikexCard.formPoaDoc")} required hint={t("spikexCard.formImageHint")} value={formState.poaDoc} onChange={(value) => updateField("poaDoc", value)} />
              ) : null}
              <div className={styles.reviewCard}>
                <div className={styles.reviewRow}>
                  <span>{t("spikexCard.formFreezeAmount")}</span>
                  <strong>{freezeAmountLabel}</strong>
                </div>
                <p className={styles.reviewHint}>{t("spikexCard.formFreezeAmountHint")}</p>
              </div>
            </div>
          ) : null}
        </div>

        <div className={styles.modalFooter}>
          <button type="button" className={styles.modalGhostBtn} onClick={currentStep === 0 ? onClose : handlePrev}>
            {currentStep === 0 ? t("spikexCard.formCancel") : t("spikexCard.formPrev")}
          </button>
          {currentStep < FORM_STEPS.length - 1 ? (
            <button type="button" className={styles.modalPrimaryBtn} onClick={handleNext}>
              {t("spikexCard.formNext")}
            </button>
          ) : (
            <button type="button" className={styles.modalPrimaryBtn} onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Spin size="small" /> : t("spikexCard.formSubmit")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SpikexCard() {
  const t = useTranslation();
  const router = useRouter();
  const { isDesktop, isReady } = useIsDesktop();
  const isSpikexPc = isReady && isDesktop;
  const isInApp = Platform.isInApp();
  const [appState] = useContext(XtContext);
  const { loginStatus, userInfo, authInfo } = appState || {};

  const mockLevel = useMemo(() => parseSpikexCardMockLevel(router.query), [router.query?.mockLevel]);
  const mockEntryStatusKey = useMemo(
    () => parseSpikexCardMockEntryStatus(router.query),
    [router.query?.status, router.query?.applyStatus]
  );
  const mockApplyStatus = useMemo(
    () => parseSpikexCardMockApplyStatus(router.query),
    [router.query?.status, router.query?.applyStatus]
  );
  const mockVipProgress = useMemo(
    () => (mockLevel != null ? buildMockVipProgress(mockLevel) : null),
    [mockLevel]
  );
  const mockEligibility = useMemo(() => {
    if (mockLevel == null && mockEntryStatusKey == null) return null;
    const level = mockLevel ?? SPIKEX_CARD_UNLOCK_LEVEL;
    return buildMockEligibility(level);
  }, [mockLevel, mockEntryStatusKey]);
  const mockEntryStatus = useMemo(() => {
    if (mockEntryStatusKey != null) {
      return buildMockEntryStatus(mockEntryStatusKey, { applySubStatus: mockApplyStatus });
    }
    if (mockLevel != null) {
      return resolveMockEntryStatusFromLevel(mockLevel, null, null);
    }
    return null;
  }, [mockLevel, mockEntryStatusKey, mockApplyStatus]);
  const mockApplyStatusData = useMemo(() => {
    if (mockEntryStatus == null) return null;
    const entryKey = mockEntryStatus.status;
    const applyKey = mockApplyStatus ?? mockEntryStatus._mockApplySubStatus;

    if (entryKey === "REVIEWING") {
      return buildMockApplyStatus(applyKey || "REVIEWING", entryKey);
    }
    if (entryKey === "WAIT_ATTACHMENT") {
      return buildMockApplyStatus(applyKey || "WAIT_ATTACHMENT", entryKey);
    }
    if (entryKey === "REJECTED") {
      return buildMockApplyStatus(applyKey || "REJECTED", entryKey);
    }
    return null;
  }, [mockEntryStatus, mockApplyStatus]);
  const mockCardBalance = useMemo(() => {
    if (mockEntryStatus == null) return null;
    if (mockEntryStatus.status !== "ACTIVE") return null;
    return buildMockCardBalance(mockEntryStatus);
  }, [mockEntryStatus]);

  const initialPageCacheRef = useRef(undefined);
  if (initialPageCacheRef.current === undefined) {
    if (mockEntryStatus != null || loginStatus !== 1) {
      initialPageCacheRef.current = null;
    } else {
      const cacheUserKey = getSpikexCardPageCacheUserKey(userInfo);
      initialPageCacheRef.current = cacheUserKey ? readSpikexCardPageCache(cacheUserKey) : null;
    }
  }
  const initialPageCache = initialPageCacheRef.current;
  const pageCacheHydratedRef = useRef(Boolean(initialPageCache));

  const userKey = useMemo(
    () => (loginStatus === 1 ? getSpikexCardPageCacheUserKey(userInfo) : null),
    [loginStatus, userInfo?.userId]
  );

  const [entryBootstrapDone, setEntryBootstrapDone] = useState(
    () => mockEntryStatus != null || Boolean(initialPageCache?.entryStatus)
  );
  const [vipProgress, setVipProgress] = useState(() => initialPageCache?.vipProgress ?? null);
  const [eligibility, setEligibility] = useState(() => initialPageCache?.eligibility ?? null);
  const [entryStatus, setEntryStatus] = useState(() => initialPageCache?.entryStatus ?? null);
  const [cardInfo, setCardInfo] = useState(() => initialPageCache?.cardInfo ?? null);
  const [cardBalance, setCardBalance] = useState(() => initialPageCache?.cardBalance ?? null);
  const [cardBalanceLoading, setCardBalanceLoading] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState(
    () => initialPageCache?.recentTransactions ?? []
  );
  const [recentTransactionsLoading, setRecentTransactionsLoading] = useState(false);
  const [cardSensitive, setCardSensitive] = useState(null);
  const [applyPrecheck, setApplyPrecheck] = useState(null);
  const [applyStatus, setApplyStatus] = useState(() => initialPageCache?.applyStatus ?? null);
  const [applying, setApplying] = useState(false);
  const [insufficientBalanceModalOpen, setInsufficientBalanceModalOpen] = useState(false);
  const [kycRequiredModalOpen, setKycRequiredModalOpen] = useState(false);
  const [insufficientBalanceTipValues, setInsufficientBalanceTipValues] = useState(null);
  const [submittingAttachment, setSubmittingAttachment] = useState(false);
  const [attachmentType, setAttachmentType] = useState(1);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formStep, setFormStep] = useState(0);
  const [formState, setFormState] = useState(buildEmptyFormState);
  const [kycDetail, setKycDetail] = useState(null);
  const hasVipRef = useRef(false);

  const effectiveVipProgress = mockVipProgress ?? vipProgress;
  const effectiveEligibility = mockEligibility ?? eligibility;
  const effectiveEntryStatus = mockEntryStatus ?? entryStatus;
  const effectiveApplyStatus = mockApplyStatusData ?? applyStatus;
  const effectiveCardBalance = mockCardBalance ?? cardBalance;
  const pageStatus = useMemo(
    () => resolveSpikexCardPageStatus(effectiveEntryStatus, effectiveEligibility),
    [effectiveEntryStatus, effectiveEligibility]
  );
  const levelData = useMemo(
    () => buildLevelDisplay(effectiveVipProgress, pageStatus, effectiveEligibility),
    [effectiveVipProgress, pageStatus, effectiveEligibility]
  );
  const displayUnlockLevel = useMemo(
    () => resolveSpikexCardDisplayUnlockLevel(effectiveEligibility),
    [effectiveEligibility]
  );
  const isUnlocked = useMemo(
    () => resolveSpikexCardUnlocked(effectiveEntryStatus, effectiveEligibility),
    [effectiveEntryStatus, effectiveEligibility]
  );
  const displayBalance = useMemo(
    () => resolveDisplayCardBalance(effectiveCardBalance, effectiveEntryStatus),
    [effectiveCardBalance, effectiveEntryStatus]
  );
  const applyPhase = useMemo(
    () => resolveSpikexCardApplyPhase(effectiveEntryStatus),
    [effectiveEntryStatus]
  );
  const statusUi = useMemo(
    () => resolveEntryStatusUi(effectiveEntryStatus, effectiveApplyStatus),
    [effectiveEntryStatus, effectiveApplyStatus]
  );
  const statusContext = useMemo(
    () => buildStatusDisplayContext(effectiveEntryStatus, effectiveApplyStatus),
    [effectiveEntryStatus, effectiveApplyStatus]
  );
  const postApplyView = useMemo(
    () => (isUnlocked ? resolvePostApplyView(applyPhase, statusUi) : null),
    [isUnlocked, applyPhase, statusUi]
  );
  const feeCards = useMemo(() => buildFeeCards(cardInfo), [cardInfo]);
  const hasFixedFooter = Boolean(postApplyView && postApplyView.footer && postApplyView.footer !== "none");
  const needPoa = Number(cardInfo?.needPoa ?? cardInfo?.need_poa ?? 0) === 1;

  const fetchCardBalance = useCallback(async (entry, { background = false } = {}) => {
    const hyperPayCardId = entry?.hyperPayCardId;
    const status = entry?.status;
    if (!hyperPayCardId || status !== "ACTIVE") {
      setCardBalance(null);
      setCardBalanceLoading(false);
      return null;
    }
    if (mockCardBalance != null) {
      setCardBalance(mockCardBalance);
      setCardBalanceLoading(false);
      return mockCardBalance;
    }

    if (!background) {
      setCardBalanceLoading(true);
    }
    try {
      const result = await get_hyperpayCardBalance(hyperPayCardId, { isAutoPopError: false });
      const next = normalizeHyperpayCardBalance(result);
      setCardBalance(next);
      return next;
    } catch (_) {
      if (!background) setCardBalance(null);
      return null;
    } finally {
      setCardBalanceLoading(false);
    }
  }, [mockCardBalance]);

  const fetchRecentTransactions = useCallback(async (entry, { background = false } = {}) => {
    const hyperPayCardId = entry?.hyperPayCardId;
    const status = entry?.status;
    if (!hyperPayCardId || status !== "ACTIVE") {
      setRecentTransactions([]);
      setRecentTransactionsLoading(false);
      return [];
    }
    if (mockEntryStatus != null) {
      const mock = buildMockRecentTransactions();
      setRecentTransactions(mock);
      setRecentTransactionsLoading(false);
      return mock;
    }

    if (!background) {
      setRecentTransactionsLoading(true);
    }
    try {
      const list = await fetchRecentHyperpayTransactions(hyperPayCardId, 5);
      setRecentTransactions(list);
      return list;
    } catch (_) {
      if (!background) setRecentTransactions([]);
      return [];
    } finally {
      setRecentTransactionsLoading(false);
    }
  }, [mockEntryStatus]);

  const clearCardSensitive = useCallback(() => {
    setCardSensitive(null);
  }, []);

  const fetchCardSensitive = useCallback(async () => {
    const hyperPayCardId = effectiveEntryStatus?.hyperPayCardId;
    const status = effectiveEntryStatus?.status;
    if (!hyperPayCardId || status !== "ACTIVE") {
      message.error(t("spikexCard.cardInfoUnavailable"));
      return null;
    }

    if (mockEntryStatus != null) {
      const mock = buildMockCardSensitive();
      setCardSensitive(mock);
      return mock;
    }

    try {
      const result = await post_hyperpayCardSensitive(hyperPayCardId, { isAutoPopError: false });
      const next = result && typeof result === "object" ? result : null;
      if (!next) {
        message.error(t("spikexCard.cardInfoUnavailable"));
        return null;
      }
      setCardSensitive(next);
      return next;
    } catch (error) {
      message.error(resolveHyperpayErrorMessage(t, error, "spikexCard.cardInfoUnavailable"));
      return null;
    }
  }, [effectiveEntryStatus?.hyperPayCardId, effectiveEntryStatus?.status, mockEntryStatus, t]);

  const fetchEligibility = useCallback(async () => {
    if (loginStatus !== 1) {
      setEligibility(null);
      return null;
    }

    try {
      const result = await get_hyperpayCardEligibility({ isAutoPopError: false });
      setEligibility(result && typeof result === "object" ? result : null);
      return result || null;
    } catch (_) {
      setEligibility(null);
      return null;
    }
  }, [loginStatus]);

  const fetchApplyStatusForEntry = useCallback(
    async (entry) => {
      if (loginStatus !== 1 || mockApplyStatusData != null) {
        if (mockApplyStatusData == null) setApplyStatus(null);
        return null;
      }

      const entryKey = entry?.status;
      if (entryKey !== "REVIEWING" && entryKey !== "WAIT_ATTACHMENT") {
        setApplyStatus(null);
        return null;
      }

      try {
        const result = await post_hyperpayCardApplyStatus({ isAutoPopError: false });
        setApplyStatus(result || null);
        return result || null;
      } catch (_) {
        setApplyStatus(null);
        return null;
      }
    },
    [loginStatus, mockApplyStatusData]
  );

  const fetchEntryStatus = useCallback(async ({ background = false } = {}) => {
    if (loginStatus !== 1 || mockEntryStatus != null) {
      if (mockEntryStatus == null) setEntryStatus(null);
      setEntryBootstrapDone(true);
      return null;
    }

    try {
      const result = await get_hyperpayCardEntryStatus({ isAutoPopError: false });
      const next = result && typeof result === "object" ? result : null;
      setEntryStatus(next);
      await fetchApplyStatusForEntry(next);
      await fetchCardBalance(next, { background });
      return next;
    } catch (_) {
      setEntryStatus(null);
      setApplyStatus(null);
      return null;
    } finally {
      setEntryBootstrapDone(true);
    }
  }, [loginStatus, mockEntryStatus, fetchApplyStatusForEntry, fetchCardBalance]);

  const fetchVipProgress = useCallback(async () => {
    try {
      const vip = await get_inviteVipProgress();
      hasVipRef.current = true;
      setVipProgress(normalizeVipProgress(vip));
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  const fetchCardInfo = useCallback(async () => {
    try {
      const info = await get_hyperpayCardInfo();
      setCardInfo(info ?? null);
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  const fetchApplyPrecheck = useCallback(async ({ entry, force = false } = {}) => {
    if (loginStatus !== 1) {
      setApplyPrecheck(null);
      return null;
    }

    const status = entry?.status ?? effectiveEntryStatus?.status;
    if (!force && status !== "NO_CARD") {
      setApplyPrecheck(null);
      return null;
    }

    try {
      const result = await get_hyperpayCardApplyPrecheck({ isAutoPopError: false });
      const next = result && typeof result === "object" ? result : null;
      setApplyPrecheck(next);
      return next;
    } catch (_) {
      setApplyPrecheck(null);
      return null;
    }
  }, [loginStatus, effectiveEntryStatus?.status]);

  const fetchKycDetail = useCallback(async () => {
    try {
      const detail = await get_resetKycInfo({ isAutoPopError: false });
      setKycDetail(detail || null);
      return detail || null;
    } catch (_) {
      setKycDetail(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (mockLevel != null) return undefined;
    fetchCardInfo();
    return undefined;
  }, [mockLevel, fetchCardInfo]);

  useEffect(() => {
    if (pageCacheHydratedRef.current || mockEntryStatus != null || !userKey) return undefined;
    const snapshot = readSpikexCardPageCache(userKey);
    if (!snapshot) return undefined;

    pageCacheHydratedRef.current = true;
    setVipProgress((prev) => prev ?? snapshot.vipProgress ?? null);
    setEligibility((prev) => prev ?? snapshot.eligibility ?? null);
    setEntryStatus((prev) => prev ?? snapshot.entryStatus ?? null);
    setCardInfo((prev) => prev ?? snapshot.cardInfo ?? null);
    setCardBalance((prev) => prev ?? snapshot.cardBalance ?? null);
    setRecentTransactions((prev) => (prev.length ? prev : snapshot.recentTransactions ?? []));
    setApplyStatus((prev) => prev ?? snapshot.applyStatus ?? null);
    if (snapshot.entryStatus) setEntryBootstrapDone(true);
    return undefined;
  }, [userKey, mockEntryStatus]);

  useEffect(() => {
    if (!userKey || mockEntryStatus != null || loginStatus !== 1) return undefined;
    writeSpikexCardPageCache(userKey, {
      vipProgress,
      eligibility,
      entryStatus,
      cardInfo,
      cardBalance,
      recentTransactions,
      applyStatus,
      applyPrecheck,
    });
    return undefined;
  }, [
    userKey,
    loginStatus,
    mockEntryStatus,
    vipProgress,
    eligibility,
    entryStatus,
    cardInfo,
    cardBalance,
    recentTransactions,
    applyStatus,
    applyPrecheck,
  ]);

  useEffect(() => {
    if (loginStatus === 0) {
      clearSpikexCardPageCache(userKey);
      hasVipRef.current = false;
      setVipProgress(null);
      setEligibility(null);
      setEntryStatus(null);
      setEntryBootstrapDone(false);
      if (mockApplyStatusData == null) setApplyStatus(null);
      setApplyPrecheck(null);
      setCardBalance(null);
      setRecentTransactions([]);
      return;
    }
    if (loginStatus !== 1) return;
    const hasCachedEntry = Boolean(readSpikexCardPageCache(userKey)?.entryStatus);
    fetchEligibility();
    if (mockEntryStatus == null) {
      fetchEntryStatus({ background: hasCachedEntry });
    }
  }, [
    loginStatus,
    userKey,
    fetchEligibility,
    fetchEntryStatus,
    mockEntryStatus,
    mockApplyStatusData,
  ]);

  useEffect(() => {
    if (loginStatus !== 1) return undefined;
    const status = effectiveEntryStatus?.status;
    if (!status) return undefined;
    if (status === "NO_CARD") {
      fetchApplyPrecheck({ entry: effectiveEntryStatus });
    } else {
      setApplyPrecheck(null);
    }
    return undefined;
  }, [loginStatus, effectiveEntryStatus?.status, fetchApplyPrecheck]);

  useAppWebviewReuse(
    useCallback(async () => {
      if (loginStatus !== 1 || mockEntryStatus != null) return;

      setApplyPrecheck(null);
      setInsufficientBalanceModalOpen(false);
      setInsufficientBalanceTipValues(null);
      setKycRequiredModalOpen(false);

      const entry = await fetchEntryStatus({ background: false });
      if (entry?.status === "NO_CARD") {
        await fetchApplyPrecheck({ entry, force: true });
      }
    }, [loginStatus, mockEntryStatus, fetchEntryStatus, fetchApplyPrecheck]),
    isInApp && !isSpikexPc,
  );

  useEffect(() => {
    const shouldLoadVipProgress = !resolveSpikexCardApplyEligibility(effectiveEligibility);
    if (!shouldLoadVipProgress) return undefined;
    if (!hasVipRef.current || mockLevel != null) {
      fetchVipProgress();
    }
    return undefined;
  }, [effectiveEligibility, mockLevel, fetchVipProgress]);

  useEffect(() => {
    const onVisible = () => {
      if (document.hidden) {
        clearCardSensitive();
        return;
      }
      if (loginStatus === 1) {
        fetchCardInfo();
        fetchEligibility();
        if (mockEntryStatus == null) fetchEntryStatus();
        if (effectiveEntryStatus?.status === "NO_CARD") {
          fetchApplyPrecheck({ entry: effectiveEntryStatus });
        }
        if (isCardManagementPhase(applyPhase) && mockEntryStatus == null) {
          if (effectiveEntryStatus?.status === "ACTIVE") {
            fetchCardBalance(effectiveEntryStatus, { background: true });
            fetchRecentTransactions(effectiveEntryStatus, { background: true });
          }
        }
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [
    loginStatus,
    applyPhase,
    effectiveEntryStatus,
    fetchCardInfo,
    fetchEligibility,
    fetchEntryStatus,
    fetchApplyPrecheck,
    fetchCardBalance,
    fetchRecentTransactions,
    clearCardSensitive,
    mockEntryStatus,
  ]);

  useEffect(() => {
    if (!isCardManagementPhase(applyPhase)) return undefined;
    if (!effectiveEntryStatus?.hyperPayCardId) return undefined;
    if (loginStatus !== 1 && mockEntryStatus == null) return undefined;
    if (effectiveEntryStatus?.status !== "ACTIVE") {
      setCardBalance(null);
      setRecentTransactions([]);
      return undefined;
    }
    fetchCardBalance(effectiveEntryStatus, {
      background: Boolean(initialPageCache?.cardBalance),
    });
    fetchRecentTransactions(effectiveEntryStatus, {
      background: Boolean((initialPageCache?.recentTransactions?.length ?? 0) > 0),
    });
    return undefined;
  }, [
    loginStatus,
    applyPhase,
    effectiveEntryStatus?.hyperPayCardId,
    effectiveEntryStatus?.status,
    mockEntryStatus,
    fetchCardBalance,
    fetchRecentTransactions,
  ]);

  useEffect(() => {
    if (applyPhase !== SPIKEX_CARD_APPLY_PHASES.ACTIVE) {
      clearCardSensitive();
    }
    return undefined;
  }, [applyPhase, clearCardSensitive]);

  useEffect(() => {
    if (!formOpen) return;
    const prefill = buildPrefillFormState({ userInfo, authInfo, kycInfo: kycDetail });
    setFormState({ ...buildEmptyFormState(), ...prefill });
  }, [formOpen, userInfo, authInfo, kycDetail]);

  useEffect(() => {
    if (applyPhase !== "attachment") {
      setAttachmentType(1);
      setAttachmentFile(null);
    }
  }, [applyPhase, effectiveEntryStatus?.status]);

  const handleViewUpgradePath = () => {
    router.push("/referral");
  };

  const redirectToKyc = () => {
    toVerification(router.locale);
  };

  const handleKycGoVerify = () => {
    setKycRequiredModalOpen(false);
    redirectToKyc();
  };

  const handleApply = async () => {
    if (isLoginStatusPending(loginStatus)) return;
    if (loginStatus !== 1) {
      navigateAccountsLogin();
      return;
    }
    if (applying) return;

    const kycGateStatus = resolveKycGateStatus(authInfo, userInfo);
    if (kycGateStatus === KYC_REVIEWING_STATUS) {
      message.info(t("spikexCard.kycReviewingTip"));
      return;
    }
    if (kycGateStatus !== KYC_APPROVED_STATUS) {
      setKycRequiredModalOpen(true);
      return;
    }
    if (!resolveSpikexCardApplyEligibility(effectiveEligibility)) {
      message.warning(t("spikexCard.applyNotEligible"));
      return;
    }

    let precheckEntries = await fetchApplyPrecheckForCurrencies(
      get_hyperpayCardApplyPrecheck,
      cardInfo
    );
    if (!resolveCanProceedWithApplyPrechecks(precheckEntries)) {
      const tipValues = buildApplyPrecheckInsufficientTipValuesMulti(
        cardInfo,
        precheckEntries,
        effectiveEligibility
      );
      setInsufficientBalanceTipValues(tipValues);
      setInsufficientBalanceModalOpen(true);
      return;
    }

    router.push("/spikex-card/apply");
  };

  const handleInsufficientBalanceDepositUsdt = () => {
    setInsufficientBalanceModalOpen(false);
    navigateSpikexCardDeposit(HYPERPAY_PAY_CURRENCIES.USDT, { isSpikexPc });
  };

  const handleInsufficientBalanceDepositBrl = () => {
    setInsufficientBalanceModalOpen(false);
    navigateSpikexCardDeposit(HYPERPAY_PAY_CURRENCIES.BRL, { isSpikexPc });
  };

  const handleReapply = () => {
    handleApply();
  };

  const handleBindCard = () => {
    message.info(t("spikexCard.bindCardTip"));
  };

  const handleTopUp = () => {
    if (effectiveEntryStatus?.status !== "ACTIVE") {
      message.warning(t("spikexCard.frozenUnavailableHint"));
      return;
    }
    const hyperPayCardId = effectiveEntryStatus?.hyperPayCardId;
    const cardInfoId = effectiveEntryStatus?.cardId;
    if (!hyperPayCardId || cardInfoId == null) {
      message.error(t("spikexCard.rechargeUnavailable"));
      return;
    }
    const query = {
      hyperPayCardId: String(hyperPayCardId),
      cardInfoId: String(cardInfoId),
    };
    pushSpikexCardRoute(router, "/spikex-card/recharge", query);
  };

  const handleViewAllTransactions = () => {
    if (effectiveEntryStatus?.status !== "ACTIVE") {
      message.warning(t("spikexCard.transactionsUnavailable"));
      return;
    }
    const hyperPayCardId = effectiveEntryStatus?.hyperPayCardId;
    const cardInfoId = effectiveEntryStatus?.cardId;
    if (!hyperPayCardId || cardInfoId == null) {
      message.error(t("spikexCard.transactionsUnavailable"));
      return;
    }
    const query = {
      hyperPayCardId: String(hyperPayCardId),
      cardInfoId: String(cardInfoId),
    };
    if (mockEntryStatus != null) {
      query.mockTransactions = "form";
    }
    pushSpikexCardRoute(router, "/spikex-card/transactions", query);
  };

  const handleContactSupport = () => {
    openLiveHelpCustomerService();
  };

  const handleSubmitAttachment = async () => {
    if (submittingAttachment) return;

    const mcTradeNo = statusContext?.mcTradeNo;
    if (!mcTradeNo) {
      message.error(t("spikexCard.attachmentMissingTradeNo"));
      return;
    }
    if (!attachmentFile?.base64) {
      message.error(t("spikexCard.attachmentFileRequired"));
      return;
    }

    setSubmittingAttachment(true);
    try {
      await post_hyperpayCardAttachmentUpload({
        mc_trade_no: mcTradeNo,
        stage: 1,
        attachment: attachmentFile.base64,
        attachment_type: attachmentType,
      });
      message.success(t("spikexCard.attachmentSubmitSuccess"));
      setAttachmentFile(null);
      if (mockEntryStatus == null) await fetchEntryStatus();
    } catch (error) {
      message.error(resolveHyperpayErrorMessage(t, error, "HYPERPAY_ATTACH_UPLOAD_FAILED"));
    } finally {
      setSubmittingAttachment(false);
    }
  };

  const handleCloseForm = () => {
    if (applying) return;
    setFormOpen(false);
  };

  const handleSubmitApply = async () => {
    if (applying) return;

    const payload = {
      base_info: {
        first_name: formState.firstName.trim(),
        last_name: formState.lastName.trim(),
        email: formState.email.trim(),
        mobile: String(formState.mobile).trim(),
        mobile_code: String(formState.mobileCode).trim(),
      },
      kyc_info: {
        birthday: formState.birthday,
        gender: Number(formState.gender),
        country_id: normalizeCountryCode(formState.countryId),
        nationality_id: normalizeCountryCode(formState.nationalityId),
        doc_type: Number(formState.docType),
        doc_no: formState.docNo.trim(),
        doc_never_expire: Number(formState.docNeverExpire),
        emergency_contact: formState.emergencyContact.trim(),
        front_doc_id: formState.frontDoc?.uploadPath,
        mix_doc_id: formState.mixDoc?.uploadPath,
        sign_img_id: formState.signImg?.uploadPath,
        address: formState.address.trim(),
        city: formState.city.trim(),
        state: formState.state.trim(),
        zip_code: formState.zipCode.trim(),
      },
    };

    if (Number(formState.docNeverExpire) !== 1) {
      payload.kyc_info.doc_expire_date = formState.docExpireDate;
    }
    if (Number(formState.docType) === 2 && formState.backDoc?.uploadPath) {
      payload.kyc_info.back_doc_id = formState.backDoc.uploadPath;
    }
    if (needPoa && formState.poaType) {
      payload.kyc_info.poa_type = Number(formState.poaType);
      if (Number(formState.poaType) === 1 && formState.poaDoc?.uploadPath) {
        payload.kyc_info.poa_doc_id = formState.poaDoc.uploadPath;
      }
    }

    setApplying(true);
    try {
      await post_hyperpayCardApply(payload);
      message.success(t("spikexCard.formSubmitSuccess"));
      setFormOpen(false);
      setFormState(buildEmptyFormState());
      setFormStep(0);
      await fetchEntryStatus();
    } catch (error) {
      message.error(resolveHyperpayErrorMessage(t, error, "HYPERPAY_APPLY_FAILED"));
    } finally {
      setApplying(false);
    }
  };

  const showBootstrapLoading =
    loginStatus === 1 && mockEntryStatus == null && !entryBootstrapDone;

  return (
    <div
      className={cx(
        styles.page,
        isSpikexPc && styles.spikexPc,
        hasFixedFooter && styles.pageApply,
      )}
    >
      <div className={cx(isSpikexPc && styles.pcContentWrap)}>
        {isSpikexPc ? <PcBackBar t={t} /> : null}

      {showBootstrapLoading ? (
        <div className={styles.pageBootstrapLoading}>
          <Spin />
        </div>
      ) : postApplyView?.showManageView ? (
        <ManageCardContent
          t={t}
          variant={postApplyView.manageVariant}
          cardBalance={effectiveCardBalance}
          cardInfo={cardInfo}
          displayBalance={displayBalance}
          balanceLoading={cardBalanceLoading}
          cardSensitive={cardSensitive}
          onFetchSensitive={fetchCardSensitive}
          onClearSensitive={clearCardSensitive}
          onRefreshBalance={() => fetchCardBalance(effectiveEntryStatus)}
          onTopUp={handleTopUp}
          onViewAllTransactions={handleViewAllTransactions}
          onContactSupport={handleContactSupport}
          recentTransactions={recentTransactions}
          recentTransactionsLoading={recentTransactionsLoading}
          spikexPc={isSpikexPc}
        />
      ) : postApplyView ? (
        isSpikexPc ? (
          <div className={styles.pcApplyBody}>
            {postApplyView.footer === "apply" ? (
              <PcEligibleApplyLayout
                t={t}
                levelData={levelData}
                postApplyView={postApplyView}
                displayUnlockLevel={displayUnlockLevel}
                isUnlocked={isUnlocked}
                feeCards={feeCards}
                cardInfo={cardInfo}
                footerProps={{
                  t,
                  onApply: handleApply,
                  onReapply: handleReapply,
                  onSubmitAttachment: handleSubmitAttachment,
                  onBindCard: handleBindCard,
                  onTopUp: handleTopUp,
                  busy: applying || submittingAttachment,
                }}
              />
            ) : (
              <PcInProgressApplyLayout
                t={t}
                postApplyView={postApplyView}
                displayUnlockLevel={displayUnlockLevel}
                isUnlocked={isUnlocked}
                entryStatus={effectiveEntryStatus}
                applyStatus={effectiveApplyStatus}
                statusUi={statusUi}
                statusContext={statusContext}
                attachmentType={attachmentType}
                attachmentFile={attachmentFile}
                onAttachmentTypeChange={setAttachmentType}
                onAttachmentFileChange={setAttachmentFile}
                feeCards={feeCards}
                footerProps={{
                  t,
                  onApply: handleApply,
                  onReapply: handleReapply,
                  onSubmitAttachment: handleSubmitAttachment,
                  onBindCard: handleBindCard,
                  onTopUp: handleTopUp,
                  busy: applying || submittingAttachment,
                }}
              />
            )}
          </div>
        ) : (
          <>
            <CardVisual
              t={t}
              unlockLevel={displayUnlockLevel}
              isUnlocked={isUnlocked}
            />
            <PostApplyContent
              t={t}
              levelData={levelData}
              postApplyView={postApplyView}
              entryStatus={effectiveEntryStatus}
              applyStatus={effectiveApplyStatus}
              statusUi={statusUi}
              statusContext={statusContext}
              feeCards={feeCards}
              cardInfo={cardInfo}
              attachmentType={attachmentType}
              attachmentFile={attachmentFile}
              onAttachmentTypeChange={setAttachmentType}
              onAttachmentFileChange={setAttachmentFile}
            />
            <CardFooter
              t={t}
              variant={postApplyView.footer}
              footerLabelKey={postApplyView.footerLabelKey}
              onApply={handleApply}
              onReapply={handleReapply}
              onSubmitAttachment={handleSubmitAttachment}
              onBindCard={handleBindCard}
              onTopUp={handleTopUp}
              busy={applying || submittingAttachment}
            />
          </>
        )
      ) : isSpikexPc ? (
        <div className={styles.pcGrid}>
          <div className={styles.pcLeftPanel}>
            <div className={styles.pcCardVisualWrap}>
              <CardVisual
                t={t}
                unlockLevel={displayUnlockLevel}
                isUnlocked={isUnlocked}
                spikexPc
              />
            </div>
            <LevelSection t={t} levelData={levelData} />
            <div className={styles.pcLeftDivider} />
            <ProgressSection
              t={t}
              levelData={levelData}
              onViewUpgradePath={handleViewUpgradePath}
              showRequirementsTitle
            />
          </div>
          <BenefitsPromoPanel t={t} />
        </div>
      ) : (
        <>
          <CardVisual
            t={t}
            unlockLevel={displayUnlockLevel}
            isUnlocked={isUnlocked}
          />
          <UnlockStateContent t={t} levelData={levelData} onViewUpgradePath={handleViewUpgradePath} />
        </>
      )}

      </div>

      <Modal
        className={`${styles.insufficientBalanceModal} ${modalStyles.modalRoot}`}
        wrapClassName={modalStyles.modalWrap}
        open={insufficientBalanceModalOpen}
        onCancel={() => setInsufficientBalanceModalOpen(false)}
        footer={null}
        centered
        closable={false}
        width={isSpikexPc ? 440 : 327}
        maskClosable
      >
        <div className={modalStyles.modalBody}>
          <div className={modalStyles.modalTitle}>
            {t("spikexCard.applyPrecheckInsufficientTitle")}
          </div>
          <div className={modalStyles.modalDesc}>
            {t(
              insufficientBalanceTipValues?.isMultiCurrency
                ? insufficientBalanceTipValues?.hasIssuedFee
                  ? "spikexCard.applyPrecheckInsufficientBothWithFee"
                  : "spikexCard.applyPrecheckInsufficientBothFeeWaived"
                : insufficientBalanceTipValues?.hasIssuedFee
                  ? "spikexCard.applyPrecheckInsufficientWithFee"
                  : "spikexCard.applyPrecheckInsufficientFeeWaived",
              insufficientBalanceTipValues || {}
            )}
          </div>
          <div className={modalStyles.modalActions}>
            <button
              type="button"
              className={modalStyles.btnGhost}
              onClick={handleInsufficientBalanceDepositBrl}
            >
              {t("spikexCard.applyPrecheckGoDepositBrl")}
            </button>
            <button
              type="button"
              className={modalStyles.btnPrimary}
              onClick={handleInsufficientBalanceDepositUsdt}
            >
              {t("spikexCard.applyPrecheckGoDepositUsdt")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        className={`${styles.insufficientBalanceModal} ${modalStyles.modalRoot}`}
        wrapClassName={modalStyles.modalWrap}
        open={kycRequiredModalOpen}
        onCancel={() => setKycRequiredModalOpen(false)}
        footer={null}
        centered
        closable={false}
        width={isSpikexPc ? 440 : 327}
        maskClosable
      >
        <div className={modalStyles.modalBody}>
          <div className={modalStyles.modalTitle}>{t("spikexCard.kycRequiredTitle")}</div>
          <div className={modalStyles.modalDesc}>{t("spikexCard.kycRequiredTip")}</div>
          <div className={modalStyles.modalActions}>
            <button
              type="button"
              className={modalStyles.btnGhost}
              onClick={() => setKycRequiredModalOpen(false)}
            >
              {t("cancel")}
            </button>
            <button type="button" className={modalStyles.btnPrimary} onClick={handleKycGoVerify}>
              {t("spikexCard.kycGoVerify")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
